import { useEffect, useState, useMemo } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { fetchCuestionarioById } from '../../api/cuestionariosApi';
import { FRECUENCIA_SCORES, CANTIDAD_SCORES, calcularPuntuacionICIQ } from './useCuestionarioSPForm';

const parseRespuestas = (value) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch (_e) { return {}; }
  }
  return typeof value === 'object' ? value : {};
};

const unwrapEntity = (payload) => payload?.data ?? payload;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

export const OPCIONES_FRECUENCIA = [
  'Nunca',
  'Una vez a la semana',
  '2-3 veces/semana',
  'Una vez al día',
  'Varias veces al día',
  'Continuamente',
];

export const OPCIONES_CANTIDAD = [
  'No se me escapa nada',
  'Muy poca cantidad',
  'Una cantidad moderada',
  'Mucha cantidad',
];

export const useCuestionarioSPViewer = ({ idCuestionario, paciente }) => {
  const [patientInfo, setPatientInfo] = useState({ nombre: '', apellidos: '', createdAt: '' });
  const [rawScores, setRawScores] = useState({ frecuencia: '', cantidad: '', impactoVida: 0 });
  const [fields, setFields] = useState({
    frecuencia: '',
    cantidad: '',
    impactoVida: '',
    factores: [],
  });
  const [openGood, setOpenGood] = useState(false);

  useEffect(() => {
    if (!idCuestionario || !paciente) return;
    fetchCuestionarioById(idCuestionario, paciente)
      .then((rawData) => {
        const data = unwrapEntity(rawData);
        const r = parseRespuestas(data.respuestas);
        setPatientInfo({
          nombre: data?.paciente?.firstname || '',
          apellidos: data?.paciente?.lastname || '',
          createdAt: formatDate(data.createdAt),
        });
        setRawScores({
          frecuencia: r.frecuencia ?? '',
          cantidad: r.cantidad ?? '',
          impactoVida: r.impactoVida ?? 0,
        });
        setFields({
          frecuencia: OPCIONES_FRECUENCIA[parseInt(r.frecuencia, 10)] || r.frecuencia || '',
          cantidad: OPCIONES_CANTIDAD[parseInt(r.cantidad, 10)] || r.cantidad || '',
          impactoVida: String(r.impactoVida ?? ''),
          factores: r.factores || [],
        });
      })
      .catch(console.error);
  }, [idCuestionario, paciente]);

  const pFrecuencia = useMemo(
    () => rawScores.frecuencia !== '' ? (FRECUENCIA_SCORES[parseInt(rawScores.frecuencia, 10)] ?? null) : null,
    [rawScores.frecuencia],
  );
  const pCantidad = useMemo(
    () => rawScores.cantidad !== '' ? (CANTIDAD_SCORES[parseInt(rawScores.cantidad, 10)] ?? null) : null,
    [rawScores.cantidad],
  );
  const puntuacionTotal = useMemo(
    () => calcularPuntuacionICIQ(rawScores.frecuencia, rawScores.cantidad, rawScores.impactoVida),
    [rawScores],
  );

  const handleExportPDF = () => {
    const input = document.getElementById('pdf-content');
    html2canvas(input, {
      scale: 2,
      backgroundColor: null,
      useCORS: true,
      ignoreElements: (el) => el.tagName === 'BUTTON',
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`cuestionario_sp_${patientInfo.nombre}${patientInfo.apellidos}_${patientInfo.createdAt}.pdf`);
      })
      .catch(console.error);
  };

  return {
    patientInfo,
    fields,
    puntuacionTotal,
    pFrecuencia,
    pCantidad,
    openGood,
    setOpenGood,
    handleExportPDF,
  };
};
